import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommentModeration = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('flagged');

  useEffect(() => {
    fetchComments();
  }, [filterStatus]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('blog_comments')?.select(`
          *,
          blogs!inner(title, slug)
        `)?.order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query?.eq('status', filterStatus);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setComments(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError(err?.message || 'Failed to fetch comments');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (commentId, newStatus) => {
    try {
      const { error: updateError } = await supabase?.from('blog_comments')?.update({
          status: newStatus,
          moderated_at: new Date()?.toISOString(),
          moderated_by: 'Admin'
        })?.eq('id', commentId);

      if (updateError) throw updateError;
      await fetchComments();
    } catch (err) {
      console.error('Error moderating comment:', err);
      alert('Failed to moderate comment: ' + err?.message);
    }
  };


  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success text-white';
      case 'pending':
        return 'bg-warning text-white';
      case 'rejected':
        return 'bg-error text-white';
      case 'flagged':
        return 'bg-orange-500 text-white';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Comment Moderation</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Review and moderate blog comments
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilterStatus('flagged')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'flagged' ?'bg-orange-500 text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Flagged
        </button>
        <button
          onClick={() => setFilterStatus('approved')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'approved' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Approved
        </button>
        <button
          onClick={() => setFilterStatus('rejected')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'rejected' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Rejected
        </button>
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
            filterStatus === 'all' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          All
        </button>
      </div>
      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}
      {!loading && comments?.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="MessageSquare" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            {filterStatus === 'all' ?'No comments yet.'
              : `No ${filterStatus} comments found.`}
          </p>
        </div>
      )}
      {!loading && comments?.length > 0 && (
        <div className="space-y-4">
          {comments?.map((comment) => (
            <div
              key={comment?.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-smooth"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {comment?.author_name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{comment?.author_name}</p>
                      <p className="text-xs text-muted-foreground">{comment?.author_email}</p>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        getStatusBadgeColor(comment?.status)
                      }`}
                    >
                      {comment?.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    On blog: <span className="font-medium text-foreground">{comment?.blogs?.title}</span>
                  </p>
                </div>
              </div>

              <p className="text-foreground mb-4">{comment?.comment_text}</p>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    {new Date(comment?.created_at)?.toLocaleDateString()}
                  </span>
                  {comment?.moderated_at && (
                    <span className="flex items-center gap-1">
                      <Icon name="CheckCircle" size={12} />
                      Moderated {new Date(comment?.moderated_at)?.toLocaleDateString()}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {comment?.status === 'flagged' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleModerate(comment?.id, 'approved')}
                        iconName="CheckCircle"
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleModerate(comment?.id, 'rejected')}
                        iconName="XCircle"
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {comment?.status === 'approved' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleModerate(comment?.id, 'rejected')}
                      iconName="XCircle"
                    >
                      Reject
                    </Button>
                  )}
                  {comment?.status === 'rejected' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleModerate(comment?.id, 'approved')}
                      iconName="CheckCircle"
                    >
                      Approve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentModeration;